import { ChatMessage, ProductData } from '../types/askStanley.types';
import { ASK_STANLEY_CONSTANTS } from '../constants/askStanley.constants';

/**
 * Content parsing utilities for processing chat messages with embedded markers
 * Handles extraction and segmentation of product references within message content
 */

type ContentType = (typeof ASK_STANLEY_CONSTANTS.CONTENT_TYPES)[keyof typeof ASK_STANLEY_CONSTANTS.CONTENT_TYPES];

interface ContentMarker {
    type: ContentType;
    id: number;
    start: number;
    end: number;
    content?: string;
}

export interface InstagramMainFeedPost {
    id: number;
    content: string;
}

export interface ContentPart {
    type: ContentType;
    content?: string;
    product?: ProductData;
    post?: InstagramMainFeedPost;
}

/**
 * Build regex patterns for marker extraction
 */
const buildPatterns = () => ({
    product: /\[product:(\d+)\]/g,
    mainFeedBlock: /\[instagram-main-feed-post:(\d+)\]([\s\S]*?)\[instagram-main-feed-post:\1\]/g,
});

/**
 * Extract markers from content string
 * Finds all [product:id] and [instagram-main-feed-post:id] markers and returns their positions
 */
const extractMarkers = (content: string): ContentMarker[] => {
    const { product, mainFeedBlock } = buildPatterns();
    const markers: ContentMarker[] = [];

    for (const m of content.matchAll(product)) {
        markers.push({
            type: ASK_STANLEY_CONSTANTS.CONTENT_TYPES.PRODUCT,
            id: parseInt(m[1] || '0', 10),
            start: m.index || 0,
            end: (m.index || 0) + m[0].length,
        });
    }

    for (const m of content.matchAll(mainFeedBlock)) {
        markers.push({
            type: ASK_STANLEY_CONSTANTS.CONTENT_TYPES.INSTAGRAM_MAIN_FEED_POST,
            id: parseInt(m[1] || '0', 10),
            start: m.index || 0,
            end: (m.index || 0) + m[0].length,
            content: m[2], // Content between the markers
        });
    }

    markers.sort((a, b) => a.start - b.start);
    return markers;
};

/**
 * Segment content into text and marker parts
 * Splits content around markers, preserving both text and markers
 */
const segmentContent = (content: string, markers: ContentMarker[]): ContentMarker[] => {
    const segments: ContentMarker[] = [];
    let cursor = 0;

    for (const marker of markers) {
        if (marker.start > cursor) {
            const chunk = content.slice(cursor, marker.start);
            if (chunk.trim()) {
                segments.push({
                    type: ASK_STANLEY_CONSTANTS.CONTENT_TYPES.TEXT,
                    id: -1,
                    start: cursor,
                    end: marker.start,
                    content: chunk,
                });
            }
        }
        segments.push(marker);
        cursor = marker.end;
    }

    if (cursor < content.length) {
        const tail = content.slice(cursor);
        if (tail.trim()) {
            segments.push({
                type: ASK_STANLEY_CONSTANTS.CONTENT_TYPES.TEXT,
                id: -1,
                start: cursor,
                end: content.length,
                content: tail,
            });
        }
    }
    return segments;
};

/**
 * Resolve segments to actual content parts
 * Maps segment markers to actual product data from message
 */
const resolveParts = (segments: ContentMarker[], message: ChatMessage): ContentPart[] => {
    const productMap = new Map(message.products?.map(p => [p.id, p]) || []);
    const out: ContentPart[] = [];
    for (const seg of segments) {
        if (seg.type === ASK_STANLEY_CONSTANTS.CONTENT_TYPES.TEXT) {
            out.push({ type: ASK_STANLEY_CONSTANTS.CONTENT_TYPES.TEXT, content: seg.content });
            continue;
        }
        if (seg.type === ASK_STANLEY_CONSTANTS.CONTENT_TYPES.PRODUCT) {
            // Use array index to get the product
            const product = productMap.get(seg.id);
            if (product) out.push({ type: ASK_STANLEY_CONSTANTS.CONTENT_TYPES.PRODUCT, product });
            continue;
        }
        if (seg.type === ASK_STANLEY_CONSTANTS.CONTENT_TYPES.INSTAGRAM_MAIN_FEED_POST && seg.content) {
            out.push({
                type: ASK_STANLEY_CONSTANTS.CONTENT_TYPES.INSTAGRAM_MAIN_FEED_POST,
                post: { id: seg.id, content: seg.content },
            });
            continue;
        }
    }
    return out;
};

/**
 * Parse message content into renderable parts
 * Main function that orchestrates the parsing pipeline
 *
 * @param message - The chat message to parse
 * @returns Array of content parts (text or product) ready to render
 */
export const parseMessageParts = (message: ChatMessage): ContentPart[] => {
    const content = message.content_html || message.content || '';
    if (!content || !content.trim()) return [];

    const markers = extractMarkers(content);
    const segments = segmentContent(content, markers);
    const parts = resolveParts(segments, message);

    return parts.length ? parts : [{ type: ASK_STANLEY_CONSTANTS.CONTENT_TYPES.TEXT, content }];
};
